import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { IAppVersionPolicy, IAppVersionPolicyUpdate, MobilePlatform } from 'app/shared/model/app-version-policy.model';
import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';

const initialState = {
  loading: false,
  /** Which platform's form is mid-save, so one card saving does not freeze the other. */
  updatingPlatform: null as MobilePlatform | null,
  /** Which platform was saved last, so the "saved" alert appears on that card only. */
  updateSuccessPlatform: null as MobilePlatform | null,
  errorMessage: null as string | null,
  /** Both platforms, as the backend returns them (alphabetical: android, ios). */
  policies: [] as readonly IAppVersionPolicy[],
};

export type AdminAppVersionState = Readonly<typeof initialState>;

const adminUrl = 'api/admin/app-version-policy';

export const getPolicies = createAsyncThunk(
  'adminAppVersion/fetch_policies',
  async (_: void, { signal }) => axios.get<IAppVersionPolicy[]>(adminUrl, { signal }),
  { serializeError: serializeAxiosError },
);

/**
 * Full replace of ONE platform — an omitted blockedVersions/message CLEARS it.
 *
 * The backoffice never writes both stores at once: iOS and Android are routinely out of sync (Apple review vs.
 * Google staged rollout), which is the whole reason the policy is per-platform. Write errors (bad semver,
 * min above latest, non-https URL) surface through the global notification middleware as the backend's
 * problem-detail message, so they are not duplicated into this slice.
 */
export const updatePolicy = createAsyncThunk(
  'adminAppVersion/update_policy',
  async ({ platform, body }: { platform: MobilePlatform; body: IAppVersionPolicyUpdate }) =>
    axios.put<IAppVersionPolicy>(`${adminUrl}/${platform}`, body),
  { serializeError: serializeAxiosError },
);

export const AdminAppVersionSlice = createSlice({
  name: 'adminAppVersion',
  initialState: initialState as AdminAppVersionState,
  reducers: {
    reset() {
      return initialState;
    },
    resetUpdateSuccess(state) {
      state.updateSuccessPlatform = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getPolicies.pending, state => {
        state.loading = true;
        state.errorMessage = null;
      })
      .addCase(getPolicies.fulfilled, (state, action) => {
        state.loading = false;
        state.policies = action.payload.data;
      })
      .addCase(getPolicies.rejected, (state, action) => {
        if (action.meta?.aborted) return; // superseded by a newer request — ignore
        state.loading = false;
        state.errorMessage = action.error.message!;
      })
      .addCase(updatePolicy.pending, (state, action) => {
        state.updatingPlatform = action.meta.arg.platform;
        state.updateSuccessPlatform = null;
      })
      .addCase(updatePolicy.fulfilled, (state, action) => {
        const saved = action.payload.data;
        state.updatingPlatform = null;
        state.updateSuccessPlatform = saved.platform ?? null;
        // Patch the saved platform in place: the sibling row is untouched by this write, so refetching both
        // would only risk blanking the list while it lands.
        state.policies = state.policies.map(policy => (policy.platform === saved.platform ? saved : policy));
      })
      .addCase(updatePolicy.rejected, state => {
        state.updatingPlatform = null;
        state.updateSuccessPlatform = null;
      });
  },
});

export const { reset, resetUpdateSuccess } = AdminAppVersionSlice.actions;

export default AdminAppVersionSlice.reducer;
